export type PIDControlMode = 'P' | 'PI' | 'PID';
export type PIDWorkMode = 'MANUAL' | 'AUTO';

export interface PIDParameters {
  kp: number;
  ki: number;
  kd: number;
  sampleTimeMs: number;
  minOutput: number;
  maxOutput: number;
  controlMode: PIDControlMode;
}

export interface PIDState {
  workMode: PIDWorkMode;
  pv: number;
  sp: number;
  cv: number;
  manualCv: number;
  integral: number;
  prevError: number;
  lastRunTime: number;
}

export class PIDController {
  public params: PIDParameters;
  public state: PIDState;

  constructor(params?: Partial<PIDParameters>) {
    this.params = {
      kp: 1.0,
      ki: 0.1,
      kd: 0.0,
      sampleTimeMs: 100,
      minOutput: 0,
      maxOutput: 100,
      controlMode: 'PI',
      ...params,
    };

    this.state = {
      workMode: 'MANUAL',
      pv: 0,
      sp: 50,
      cv: 0,
      manualCv: 0,
      integral: 0,
      prevError: 0,
      lastRunTime: 0,
    };
  }

  public reset() {
    this.state.integral = 0;
    this.state.prevError = 0;
    this.state.cv = 0;
    this.state.lastRunTime = 0;
  }

  public setWorkMode(mode: PIDWorkMode) {
    if (this.state.workMode === 'MANUAL' && mode === 'AUTO') {
      // Bumpless transfer: Adjust integral so that immediate output equals current manual CV
      // Assuming error = sp - pv
      const error = this.state.sp - this.state.pv;
      const pTerm = this.params.kp * error;
      this.state.integral = this.state.cv - pTerm;
      // Also clamp integral to safe limits
      this.state.integral = Math.max(this.params.minOutput, Math.min(this.params.maxOutput, this.state.integral));
      this.state.prevError = error;
    }
    this.state.workMode = mode;
  }

  public tick(simulationTimeMs: number, pv: number, sp: number): number {
    this.state.pv = pv;
    this.state.sp = sp;

    // Wait until sample time has passed
    if (this.params.sampleTimeMs > 0 && simulationTimeMs - this.state.lastRunTime < this.params.sampleTimeMs) {
      return this.state.cv; // hold output
    }

    let dt = (simulationTimeMs - this.state.lastRunTime) / 1000;
    if (dt <= 0 || isNaN(dt) || !isFinite(dt)) dt = this.params.sampleTimeMs / 1000 || 0.1;

    this.state.lastRunTime = simulationTimeMs;

    if (this.state.workMode === 'MANUAL') {
      this.state.cv = Math.max(this.params.minOutput, Math.min(this.params.maxOutput, this.state.manualCv));
      return this.state.cv;
    }

    const error = sp - pv; // Standard positive sign for Direct Action (filling a tank: if PV < SP, error is positive, output increases)

    let iTerm = 0;
    let dTerm = 0;

    const { kp, ki, kd, controlMode, minOutput, maxOutput } = this.params;

    const pTerm = kp * error;

    if (controlMode !== 'P') {
      const stepI = ki * error * dt;
      // Test if adding stepI exceeds bounds. We use conditional integration (anti-windup)
      const tentativeOutput = pTerm + this.state.integral + stepI;

      if (tentativeOutput > maxOutput && error > 0) {
        // Output saturated high, don't integrate positive errors
      } else if (tentativeOutput < minOutput && error < 0) {
        // Output saturated low, don't integrate negative errors
      } else {
        this.state.integral += stepI;
      }
      iTerm = this.state.integral;
    }

    if (controlMode === 'PID') {
      const derivative = (error - this.state.prevError) / dt;
      dTerm = kd * derivative;
    }

    let output = pTerm + iTerm + dTerm;

    if (isNaN(output) || !isFinite(output)) {
      output = 0;
    }

    // Output clamping
    if (output > maxOutput) output = maxOutput;
    else if (output < minOutput) output = minOutput;

    this.state.cv = output;
    this.state.prevError = error;

    return this.state.cv;
  }
}
